import { defineStore } from 'pinia';
import { collection, getDocs, getDoc, doc,  addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { clientSlugMatches, resolveClientBusinessName, resolveClientSlug } from '~/lib/clientSlugs';
import { buildClientPortalLink, resolveClientEmailOrigin } from '~/lib/tenantLinks';
import { normalizeClientPortalRole, resolveClientPortalPermissions } from '~/lib/clientPortalPermissions';
import type { Client, ClientContact, ClientPortalAccessRecord, ClientPortalInvitation, ClientQuickLink } from '~/types/models';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { stripUndefined } from '~/lib/stripUndefined';

type PortalAccessTarget = {
    clientId?: string;
};

export const useClientStore = defineStore('clients', {
    state: () => ({
        clients: [] as Client[],
        currentClient: null as Client | null,
        dataTenantId: null as string | null,
        loadedForTenantId: null as string | null,
    }),

    actions: {
        createClientContactId(): string {
            return `contact_${Math.random().toString(36).slice(2, 10)}`;
        },

        _normalizeClientContact(contact: Partial<ClientContact>): ClientContact | null {
            const name = String(contact.name || '').trim();
            const email = this._normalizeClientEmail(contact.email);
            const phone = String(contact.phone || '').trim();
            const role = String(contact.role || '').trim();
            const avatarURL = String(contact.avatarURL || '').trim();
            const isPrimary = contact.isPrimary === true;
            const portalRole = normalizeClientPortalRole(contact.portalRole, isPrimary);
            const projectAccessMode = isPrimary || portalRole === 'client_owner' || contact.projectAccessMode === 'all'
                ? 'all'
                : 'selected';
            const projectIds = projectAccessMode === 'selected'
                ? Array.from(new Set((Array.isArray(contact.projectIds) ? contact.projectIds : [])
                    .map((projectId) => String(projectId || '').trim())
                    .filter(Boolean)))
                : [];

            if (!name && !email && !phone && !role) {
                return null;
            }

            return {
                id: String(contact.id || this.createClientContactId()),
                name: name || email.split('@')[0] || 'Client contact',
                email,
                ...(phone ? { phone } : {}),
                ...(role ? { role } : {}),
                ...(avatarURL ? { avatarURL } : {}),
                ...(isPrimary ? { isPrimary: true } : {}),
                projectAccessMode,
                projectIds,
                portalRole,
                portalPermissions: resolveClientPortalPermissions({
                    isPrimary,
                    portalRole,
                    portalPermissions: contact.portalPermissions,
                }),
            };
        },

        _buildPrimaryContact(clientData: Partial<Client>): ClientContact | null {
            const normalized = this._normalizeClientContact({
                id: Array.isArray(clientData.contacts)
                    ? clientData.contacts.find((contact) => contact?.isPrimary)?.id
                    : undefined,
                name: clientData.contactName,
                email: clientData.email,
                phone: clientData.contactNumber || clientData.phone,
                isPrimary: true,
            });

            return normalized
                ? { ...normalized, isPrimary: true }
                : null;
        },

        _normalizeClientContacts(clientData: Partial<Client>): ClientContact[] {
            const primaryContact = this._buildPrimaryContact(clientData);
            const primaryEmail = this._normalizeClientEmail(primaryContact?.email);

            const additionalContacts = Array.isArray(clientData.contacts)
                ? clientData.contacts
                    .map((contact) => this._normalizeClientContact(contact))
                    .filter((contact): contact is ClientContact => Boolean(contact))
                    .filter((contact) => !contact.isPrimary)
                    .filter((contact) => {
                        const email = this._normalizeClientEmail(contact.email);
                        return !(primaryEmail && email && email === primaryEmail);
                    })
                : [];

            return primaryContact
                ? [primaryContact, ...additionalContacts]
                : additionalContacts;
        },

        _normalizeClientQuickLinks(quickLinks?: ClientQuickLink[] | null): ClientQuickLink[] {
            if (!Array.isArray(quickLinks)) return [];

            return quickLinks
                .map((link, index) => {
                    const label = String(link?.label || '').trim();
                    const url = String(link?.url || '').trim();
                    const color = /^#[0-9a-f]{6}$/i.test(String(link?.color || '').trim())
                        ? String(link.color).trim()
                        : '#0f766e';

                    if (!label || !url) return null;

                    return {
                        id: String(link?.id || `quick_${index + 1}`),
                        label,
                        url,
                        color,
                    };
                })
                .filter((link): link is ClientQuickLink => Boolean(link));
        },

        _primaryClientContact(clientData: Partial<Client>): ClientContact | null {
            const contacts = this._normalizeClientContacts(clientData);
            return contacts.find((contact) => contact.isPrimary) || contacts[0] || null;
        },

        _buildClientDisplayName(clientData: Partial<Client>): string {
            return resolveClientBusinessName(clientData);
        },

        _hydrateClient(clientId: string, data: Partial<Client>): Client {
            const contacts = this._normalizeClientContacts(data);
            const primaryContact = contacts.find((contact) => contact.isPrimary) || contacts[0] || null;
            const contactNumber = String(primaryContact?.phone || data.contactNumber || data.phone || '').trim();
            const headerImageURL = String(data.headerImageURL || data.profileImageURL || '').trim();
            const displayName = this._buildClientDisplayName(data) || 'Untitled client';
            const preferredSlug = resolveClientSlug({
                ...data,
                name: displayName,
            });

            return {
                id: clientId,
                tenantId: String(data.tenantId || '').trim() || undefined,
                name: displayName,
                legalName: String(data.legalName || '').trim() || undefined,
                entityType: data.entityType === 'individual' || data.entityType === 'organization'
                    ? data.entityType
                    : undefined,
                contactName: String(primaryContact?.name || data.contactName || '').trim(),
                companyName: String(data.companyName || data.businessName || '').trim(),
                website: String(data.website || '').trim() || undefined,
                address: String(data.address || '').trim(),
                email: this._normalizeClientEmail(primaryContact?.email || data.email),
                phone: contactNumber,
                contactNumber,
                ownerId: String(data.ownerId || '').trim() || undefined,
                ownerName: String(data.ownerName || '').trim() || undefined,
                lastContactedAt: data.lastContactedAt,
                inboxThreadDeletedAt: data.inboxThreadDeletedAt,
                slug: preferredSlug || String(data.slug || '').trim().toLowerCase(),
                avatarURL: String(data.avatarURL || '').trim(),
                headerImageURL,
                profileImageURL: headerImageURL,
                quickLinks: this._normalizeClientQuickLinks(data.quickLinks),
                contacts,
                intake: data.intake || undefined,
                lifecycle: data.lifecycle || undefined,
                contract: data.contract || null,
                createdAt: data.createdAt || new Date(),
                updatedAt: data.updatedAt || new Date(),
            };
        },

        _normalizeClientEmail(email?: string | null): string {
            return String(email || '').trim().toLowerCase();
        },

        _portalAccessId(tenantId: string, clientId: string): string {
            return `${tenantId}|${clientId}`;
        },

        _normalizeClientLifecycle(
            lifecycle?: Client['lifecycle'] | null,
            existingLifecycle?: Client['lifecycle'] | null,
        ): Client['lifecycle'] | undefined {
            const nextLifecycle = {
                ...(existingLifecycle || {}),
                ...(lifecycle || {}),
            };
            return Object.keys(nextLifecycle).length > 0
                ? nextLifecycle
                : undefined;
        },

        _toDate(value: unknown): Date | null {
            if (!value) return null;
            if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
                return (value as { toDate: () => Date }).toDate();
            }
            if (typeof value === 'object' && value !== null && 'seconds' in value) {
                return new Date(Number((value as { seconds: number }).seconds) * 1000);
            }
            if (value instanceof Date) return value;

            const parsed = new Date(String(value));
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        },

        /**
         * Gets the current tenant ID from the tenant store
         * @private
         */
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        /**
         * Fetches all clients for the current tenant from the Firestore database.
         * 
         * Retrieves the clients collection from the Firestore database using the current tenant ID,
         * maps the documents to objects with their IDs, and stores them in the `clients` property.
         * 
         * @async
         * @function fetchClients
         * @returns {Promise<void>}
         * @throws {Error} Logs errors to the console if the Firestore fetch operation fails
         * 
         * @example
         * await store.fetchClients();
         */
        async fetchClients(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            try {
                const tenantId = this._getTenantId();
                if (this.dataTenantId && this.dataTenantId !== tenantId) {
                    this.clients = [];
                    this.currentClient = null;
                    this.loadedForTenantId = null;
                }
                await runFreshRequest(`clients:${tenantId}:all`, async () => {
                    const collectionRef = collection($db, `tenants/${tenantId}/clients`);
                    const snapshot = await getDocs(collectionRef);
                    this.clients = snapshot.docs.map((snapshotDoc) => this._hydrateClient(snapshotDoc.id, snapshotDoc.data() as Partial<Client>));
                    this.loadedForTenantId = tenantId;
                    this.dataTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedForTenantId === tenantId,
                });
            } catch (error) {
                console.error("Error fetching clients: ", error);
            } 
        },

        async fetchClientById(clientId: string): Promise<Client | null> {
            const tenantId = this._getTenantId();
            if (this.dataTenantId && this.dataTenantId !== tenantId) {
                this.clients = [];
                this.currentClient = null;
                this.loadedForTenantId = null;
            }
            const cached = this.dataTenantId === tenantId
                ? this.clients.find(client => client.id === clientId)
                : null;
            if (cached) {
                this.currentClient = cached;
                return cached;
            }

            const { $db } = useNuxtApp();
            try {
                const docRef = doc($db, `tenants/${tenantId}/clients`, clientId);
                const snap = await getDoc(docRef);
                
                if (!snap.exists()) {
                    return null;
                }
                
                const client = this._hydrateClient(snap.id, snap.data() as Partial<Client>);
                this.currentClient = client;
                this.dataTenantId = tenantId;
                this.clients = [...this.clients.filter(item => item.id !== client.id), client];
                return client;
            } catch (error) {
                console.error('Error fetching client by ID:', error);
                return null;
            }
        },

        /**
         * Fetches a client by its slug identifier.
         * 
         * Attempts to retrieve the client from the local cache first. If not found,
         * queries the Firestore database for a client matching the given slug within
         * the current tenant's clients collection.
         * 
         * @param slug - The slug identifier of the client to fetch
         * @returns A promise that resolves to the client object if found, or null if not found or an error occurs
         * @throws Does not throw; errors are caught and logged to console
         */
        async fetchClientBySlug(slug: string): Promise<Client | null> {
            const normalizedSlug = String(slug || '').trim().toLowerCase();
            const tenantId = this._getTenantId();
            if (this.dataTenantId && this.dataTenantId !== tenantId) {
                this.clients = [];
                this.currentClient = null;
                this.loadedForTenantId = null;
            }
            const cached = this.dataTenantId === tenantId
                ? this.clients.find(client => clientSlugMatches(client, normalizedSlug))
                : null;
            if (cached) {
                this.currentClient = cached;
                return cached;
            }

            if (this.loadedForTenantId === tenantId) {
                this.currentClient = null;
                return null;
            }

            const { $db } = useNuxtApp();
            try {
                const q = query(collection($db, `tenants/${tenantId}/clients`),
                                            where("slug", "==", normalizedSlug));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    const clientDoc = snapshot.docs[0];
                    if (!clientDoc) {
                        this.currentClient = null;
                        return null;
                    }
                    const client = this._hydrateClient(clientDoc.id, clientDoc.data() as Partial<Client>);
                    this.currentClient = client;
                    this.dataTenantId = tenantId;
                    this.clients = [...this.clients.filter(item => item.id !== client.id), client];
                    return client;
                }

                await this.fetchClients();
                const fallbackClient = this.clients.find((client) => clientSlugMatches(client, normalizedSlug));
                if (!fallbackClient) {
                    this.currentClient = null;
                    return null;
                }

                this.currentClient = fallbackClient;
                return this.currentClient;

            } catch (error) {
                console.error("Error fetching client by slug: ", error);
                this.currentClient = null;
                return null;
            }
        },


        /**
         * Adds a new client to the current tenant's collection.
         * @param clientData - The client data object to add
         * @param clientData.name - The name of the client (used to generate slug)
         * @returns Promise<string> - The ID of the created client
         * @throws Logs error to console if the add operation fails
         * @example
         * await addClient({ name: 'Acme Corp', email: 'contact@acme.com' })
         */
        async addClient(clientData: Omit<Client, 'id' | 'slug' | 'createdAt' | 'updatedAt'>): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const clientRef = collection($db, `tenants/${tenantId}/clients`);

            try {
                const clientInput = clientData;
                const contacts = this._normalizeClientContacts(clientData);
                const primaryContact = contacts.find((contact) => contact.isPrimary) || contacts[0] || null;
                const normalizedEmail = this._normalizeClientEmail(primaryContact?.email || clientData.email);
                const contactNumber = String(primaryContact?.phone || clientData.contactNumber || clientData.phone || '').trim();
                const displayName = this._buildClientDisplayName(clientData) || 'Untitled client';
                const slug = resolveClientSlug({
                    ...clientInput,
                    name: displayName,
                });
                const lifecycle = this._normalizeClientLifecycle(clientInput.lifecycle);
                const client = {
                    ...clientInput,
                    name: displayName,
                    contactName: String(primaryContact?.name || clientData.contactName || '').trim(),
                    companyName: String(clientData.companyName || clientData.businessName || '').trim(),
                    address: String(clientData.address || '').trim(),
                    email: normalizedEmail,
                    phone: contactNumber,
                    contactNumber,
                    contacts,
                    avatarURL: clientData.avatarURL || '',
                    headerImageURL: clientData.headerImageURL || clientData.profileImageURL || '',
                    profileImageURL: clientData.headerImageURL || clientData.profileImageURL || '',
                    quickLinks: this._normalizeClientQuickLinks(clientData.quickLinks),
                    ...(lifecycle ? { lifecycle } : {}),
                    slug,
                    projects: [],
                    createdAt: new Date(),
                    updatedAt: new Date()
                };
                
                const docRef = await addDoc(clientRef, client);
                this.clients.push(this._hydrateClient(docRef.id, client));

                // Log activity
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    await activityStore.logActivity({
                        action: 'create',
                        entityType: 'client',
                        entityId: docRef.id,
                        description: `Created client: ${client.name}`,
                        userId: userStore.user?.id || 'unknown',
                        userName: userStore.user?.displayName,
                        userAvatar: userStore.user?.avatarURL,
                        changedField: undefined
                    });
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log client creation activity:', err);
                }

                return docRef.id;
            } catch (error) {
                console.error("Error adding client: ", error);
                throw error;
            }
        }, 

        /**
         * Updates a client by ID and syncs local store state.
         */
        async updateClient(clientId: string, updatedData: Partial<Client>): Promise<void> {
            const { $db } = useNuxtApp();
            try {
                const tenantId = this._getTenantId();
                const clientRef = doc($db, `tenants/${tenantId}/clients`, clientId);
                const clientPatch = updatedData;

                // Capture previous state for activity logging
                const previousClient = this.clients.find(c => c.id === clientId);
                const displayName = this._buildClientDisplayName({
                    ...previousClient,
                    ...clientPatch,
                });
                const normalizedContacts = Object.prototype.hasOwnProperty.call(clientPatch, 'contacts')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'contactName')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'email')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'contactNumber')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'phone')
                    ? this._normalizeClientContacts({
                        ...(previousClient || {}),
                        ...clientPatch,
                    })
                    : undefined;
                const normalizedQuickLinks = Object.prototype.hasOwnProperty.call(clientPatch, 'quickLinks')
                    ? this._normalizeClientQuickLinks(clientPatch.quickLinks)
                    : undefined;
                const primaryContact = normalizedContacts?.find((contact) => contact.isPrimary) || normalizedContacts?.[0] || null;
                const contactNumber = normalizedContacts !== undefined
                    ? String(primaryContact?.phone || '').trim()
                    : undefined;
                const headerImageURL = Object.prototype.hasOwnProperty.call(clientPatch, 'headerImageURL') || Object.prototype.hasOwnProperty.call(clientPatch, 'profileImageURL')
                    ? String(clientPatch.headerImageURL || clientPatch.profileImageURL || '').trim()
                    : undefined;
                const shouldRefreshSlug = Object.prototype.hasOwnProperty.call(clientPatch, 'companyName')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'businessName')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'name')
                    || Object.prototype.hasOwnProperty.call(clientPatch, 'contactName');
                const nextSlug = shouldRefreshSlug
                    ? resolveClientSlug({
                        ...(previousClient || {}),
                        ...clientPatch,
                        name: displayName || previousClient?.name,
                    })
                    : undefined;
                const normalizedLifecycle = this._normalizeClientLifecycle(
                    clientPatch.lifecycle,
                    previousClient?.lifecycle,
                );

                const updateData = {
                    ...clientPatch,
                    ...(displayName ? { name: displayName } : {}),
                    ...(primaryContact
                        ? {
                            contactName: primaryContact.name,
                            email: this._normalizeClientEmail(primaryContact.email),
                        }
                        : Object.prototype.hasOwnProperty.call(clientPatch, 'email')
                            ? { email: this._normalizeClientEmail(clientPatch.email) }
                            : {}),
                    ...(normalizedContacts !== undefined
                        ? { contacts: normalizedContacts }
                        : {}),
                    ...(normalizedQuickLinks !== undefined
                        ? { quickLinks: normalizedQuickLinks }
                        : {}),
                    ...(contactNumber !== undefined
                        ? {
                            phone: contactNumber,
                            contactNumber,
                        }
                        : {}),
                    ...(headerImageURL !== undefined
                        ? {
                            headerImageURL,
                            profileImageURL: headerImageURL,
                        }
                        : {}),
                    ...(nextSlug ? { slug: nextSlug } : {}),
                    ...(normalizedLifecycle ? { lifecycle: normalizedLifecycle } : {}),
                    updatedAt: new Date()
                };

                await updateDoc(clientRef, stripUndefined(updateData));

                const index = this.clients.findIndex(client => client.id === clientId);
                if (index !== -1) {
                    this.clients[index] = this._hydrateClient(clientId, { ...this.clients[index], ...updateData });
                }

                if (this.currentClient?.id === clientId) {
                    this.currentClient = this._hydrateClient(clientId, { ...this.currentClient, ...updateData });
                }

                // Log activities for changed fields
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    const changedFields = Object.keys(updatedData) as Array<keyof Partial<Client>>;
                    for (const field of changedFields) {
                        if (field === 'updatedAt') continue;
                        const previousValue = previousClient ? (previousClient[field] ?? 'unset') : 'unset';
                        const newValue = updatedData[field] ?? 'unset';
                        if (String(previousValue) !== String(newValue)) {
                            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
                            await activityStore.logActivity({
                                action: 'update',
                                entityType: 'client',
                                entityId: clientId,
                                description: `Updated ${fieldName}: ${previousValue} → ${newValue}`,
                                userId: userStore.user?.id || 'unknown',
                                userName: userStore.user?.displayName,
                                userAvatar: userStore.user?.avatarURL,
                                changedField: field as string,
                                previousValue: String(previousValue),
                                newValue: String(newValue)
                            });
                        }
                    }
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log client update activity:', err);
                }
            } catch (error) {
                console.error('Error updating client: ', error);
                throw error;
            }
        },

        async updateClientContactAccessScopes(clientId: string, contacts: ClientContact[]): Promise<void> {
            const existingClient = this.clients.find((entry) => entry.id === clientId) || this.currentClient;
            if (!existingClient) {
                throw new Error('Client not found.');
            }

            const normalizedContacts = this._normalizeClientContacts({
                ...existingClient,
                contacts,
            });

            await this.updateClient(clientId, { contacts: normalizedContacts });

            await this.syncClientPortalScopes(clientId);
        },

        /**
         * Deletes a client from the database and removes it from the local clients list.
         * @param {string} clientId - The unique identifier of the client to delete
         * @returns {Promise<void>}
         * @throws {Error} Logs error to console if deletion fails
         */
        async deleteClient(clientId: string): Promise<void> {
            const { $db } = useNuxtApp();
            try {
                const tenantId = this._getTenantId();
                const clientToDelete = this.clients.find(c => c.id === clientId);
                await deleteDoc(doc($db, `tenants/${tenantId}/clients`, clientId));
                this.clients = this.clients.filter(client => client.id !== clientId);
                if (this.currentClient?.id === clientId) {
                    this.currentClient = null;
                }

                // Log activity
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    await activityStore.logActivity({
                        action: 'delete',
                        entityType: 'client',
                        entityId: clientId,
                        description: `Deleted client: ${clientToDelete?.name || 'Unknown'}`,
                        userId: userStore.user?.id || 'unknown',
                        userName: userStore.user?.displayName,
                        userAvatar: userStore.user?.avatarURL,
                        changedField: undefined
                    });
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log client deletion activity:', err);
                }
            } catch (error) {
                console.error("Error deleting client: ", error);
                throw error;
            } 
        },

        generatePortalToken(): string {
            return Math.random().toString(36).substring(2) + Date.now().toString(36);
        },

        async _resolveInviteLink(options: {
            client?: Client | null;
            projectId?: string;
            contactId?: string;
            contactEmail?: string;
            contactName?: string;
            portalRole?: ClientContact['portalRole'];
            tenantSlug?: string;
        }): Promise<{
            inviteLink: string;
            portalRole: ClientContact['portalRole'];
            recipientName: string;
        }> {
            const { $db } = useNuxtApp();
            const tenantStore = useTenantStore();
            const tenantId = this._getTenantId();
            const client = options.client || null;
            if (!client?.id) {
                throw new Error('A client is required before sending portal invitations.');
            }

            const fallbackClientName = String(client.companyName || client.name || '').trim();
            const clientContacts = Array.isArray(client?.contacts) ? client.contacts : [];
            const recipientEmail = this._normalizeClientEmail(options.contactEmail || client?.email);
            const recipientContactId = String(options?.contactId || '').trim();
            const recipientContactName = String(options?.contactName || '').trim();
            const recipientContact = clientContacts.find((contact) => (
                (recipientContactId && contact.id === recipientContactId)
                || (recipientEmail && this._normalizeClientEmail(contact.email) === recipientEmail)
            )) || null;
            const topLevelEmail = this._normalizeClientEmail(client?.email);
            const isPrimaryContact = recipientContact?.isPrimary === true || (!recipientContact && topLevelEmail === recipientEmail);
            const scopedProjectIds = options?.projectId
                ? Array.from(new Set([
                    ...(Array.isArray(recipientContact?.projectIds) ? recipientContact.projectIds : []),
                    options.projectId,
                ].map((projectId) => String(projectId || '').trim()).filter(Boolean)))
                : Array.isArray(recipientContact?.projectIds)
                    ? recipientContact.projectIds
                    : [];
            const portalRole = normalizeClientPortalRole(options?.portalRole || recipientContact?.portalRole, isPrimaryContact);
            const portalPermissions = resolveClientPortalPermissions({
                isPrimary: isPrimaryContact,
                portalRole,
                portalPermissions: recipientContact?.portalPermissions,
            });
            const projectAccessMode = isPrimaryContact || portalRole === 'client_owner' || recipientContact?.projectAccessMode === 'all' ? 'all' : 'selected';
            const projectIds = projectAccessMode === 'selected' ? scopedProjectIds : [];

            if (!recipientEmail) {
                throw new Error('Client email is required to send a portal invitation.');
            }

            const invitationQuery = query(
                collection($db, 'clientInvitations'),
                where('tenantId', '==', tenantId),
                where('clientId', '==', client.id),
                where('email', '==', recipientEmail),
            );
            const invitationSnap = await getDocs(invitationQuery);
            const now = Date.now();
            const matchingInvitation = invitationSnap.docs
                .map((invitationDoc) => ({ id: invitationDoc.id, ...invitationDoc.data() } as ClientPortalInvitation))
                .filter((invitation) => invitation.status === 'pending')
                .filter((invitation) => {
                    const expiresAt = this._toDate(invitation.expiresAt);
                    return !expiresAt || expiresAt.getTime() > now;
                })
                .sort((a, b) => {
                    const aInvitedAt = this._toDate(a.invitedAt)?.getTime() || 0;
                    const bInvitedAt = this._toDate(b.invitedAt)?.getTime() || 0;
                    return bInvitedAt - aInvitedAt;
                })[0];
            const acceptedInvitation = invitationSnap.docs
                .map((invitationDoc) => ({ id: invitationDoc.id, ...invitationDoc.data() } as ClientPortalInvitation))
                .filter((invitation) => invitation.status === 'accepted')
                .filter((invitation) => Boolean(String(invitation.acceptedBy || '').trim()))
                .sort((a, b) => {
                    const aAcceptedAt = this._toDate(a.acceptedAt)?.getTime() || 0;
                    const bAcceptedAt = this._toDate(b.acceptedAt)?.getTime() || 0;
                    return bAcceptedAt - aAcceptedAt;
                })[0];
            const revokedInvitation = invitationSnap.docs
                .map((invitationDoc) => ({ id: invitationDoc.id, ...invitationDoc.data() } as ClientPortalInvitation))
                .filter((invitation) => invitation.status === 'revoked')
                .filter((invitation) => Boolean(String(invitation.acceptedBy || '').trim()))
                .sort((a, b) => {
                    const aRevokedAt = this._toDate(a.revokedAt)?.getTime() || 0;
                    const bRevokedAt = this._toDate(b.revokedAt)?.getTime() || 0;
                    return bRevokedAt - aRevokedAt;
                })[0];
            let token = matchingInvitation?.token;

            if (client?.id && !token && (acceptedInvitation?.acceptedBy || revokedInvitation?.acceptedBy)) {
                const { $auth } = useNuxtApp();
                const authToken = await $auth.currentUser?.getIdToken();
                if (!authToken) {
                    throw new Error('You need to be signed in to restore portal access.');
                }

                await $fetch('/api/client-portal/grant', {
                    method: 'POST',
                    body: {
                        tenantId,
                        clientId: client.id,
                        invitationId: revokedInvitation?.id || acceptedInvitation?.id,
                    },
                    headers: {
                        authorization: `Bearer ${authToken}`,
                    },
                });

                const config = useRuntimeConfig();
                const inviteOrigin = resolveClientEmailOrigin({
                    tenant: tenantStore.currentTenant,
                    appDomain: config.public.appDomain,
                    fallbackOrigin: window.location.origin,
                });
                const clientSlug = resolveClientSlug(client);
                const redirectTarget = options?.projectId
                    ? buildClientPortalLink({
                        origin: inviteOrigin,
                        clientSlug,
                        tenantId,
                        section: 'projects',
                        resourceId: options.projectId,
                    })
                    : buildClientPortalLink({
                        origin: inviteOrigin,
                        clientSlug,
                        tenantId,
                    });
                const params = new URLSearchParams({
                    redirect: redirectTarget,
                    email: recipientEmail,
                    inviteAccount: '1',
                });
                if (tenantId) {
                    params.set('tenantId', tenantId);
                }
                return {
                    inviteLink: `${inviteOrigin}/auth/client-login?${params.toString()}`,
                    portalRole,
                    recipientName: recipientContactName || String(recipientContact?.name || '').trim(),
                };
            }

            if (!token) {
                const userStore = useUserStore();
                const authStore = useAuthStore();
                const expiresAt = new Date();
                expiresAt.setDate(expiresAt.getDate() + 14);
                token = this.generatePortalToken();

                const invitation: Omit<ClientPortalInvitation, 'id'> = {
                    tenantId,
                    clientId: client.id,
                    clientName: String(client?.name || fallbackClientName || 'Client').trim(),
                    ...(recipientContactId ? { contactId: recipientContactId } : {}),
                    ...(recipientContactName ? { contactName: recipientContactName } : {}),
                    projectAccessMode,
                    projectIds,
                    portalRole,
                    portalPermissions,
                    email: recipientEmail,
                    invitedBy: authStore.user?.uid || userStore.user?.id || 'unknown',
                    invitedByName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Team',
                    invitedAt: new Date(),
                    expiresAt,
                    token,
                    status: 'pending',
                };

                await addDoc(collection($db, 'clientInvitations'), invitation);
            } else if (matchingInvitation?.id) {
                await updateDoc(doc($db, 'clientInvitations', matchingInvitation.id), {
                    clientId: client.id,
                    ...(recipientContactId ? { contactId: recipientContactId } : {}),
                    ...(recipientContactName ? { contactName: recipientContactName } : {}),
                    projectAccessMode,
                    projectIds,
                    portalRole,
                    portalPermissions,
                    updatedAt: new Date(),
                });
            }

            const config = useRuntimeConfig();
            const inviteOrigin = resolveClientEmailOrigin({
                tenant: tenantStore.currentTenant,
                appDomain: config.public.appDomain,
                fallbackOrigin: window.location.origin,
            });
            const clientSlug = resolveClientSlug(client);
            const redirectTarget = options?.projectId
                ? buildClientPortalLink({
                    origin: inviteOrigin,
                    clientSlug,
                    tenantId,
                    section: 'projects',
                    resourceId: options.projectId,
                })
                : buildClientPortalLink({
                    origin: inviteOrigin,
                    clientSlug,
                    tenantId,
                });
            const redirectQuery = redirectTarget ? `&redirect=${encodeURIComponent(redirectTarget)}` : '';

            return {
                inviteLink: `${inviteOrigin}/accept-client-invite?token=${encodeURIComponent(token)}${redirectQuery}`,
                portalRole,
                recipientName: recipientContactName || String(recipientContact?.name || '').trim(),
            };
        },

        async inviteClientToPortal(clientId: string): Promise<void> {
            return this.inviteClientContactToPortal(clientId);
        },

        async _resolveCanonicalPortalClientTarget(target: string | PortalAccessTarget): Promise<{
            client: Client | null;
            clientId?: string;
        }> {
            const explicitClientId = typeof target === 'string' ? String(target || '').trim() : String(target.clientId || '').trim();

            if (explicitClientId) {
                const client = this.clients.find((entry) => entry.id === explicitClientId)
                    || await this.fetchClientById(explicitClientId);
                return {
                    client: client || null,
                    clientId: String(client?.id || '').trim() || undefined,
                };
            }

            return { client: null };
        },

        async inviteClientContactToPortal(target: string | PortalAccessTarget, options?: {
            contactId?: string;
            contactEmail?: string;
            contactName?: string;
            portalRole?: ClientContact['portalRole'];
        }): Promise<void> {
            const tenantStore = useTenantStore();
            const userStore = useUserStore();
            const authStore = useAuthStore();

            if (!tenantStore.currentTenantRole || !['owner', 'admin'].includes(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to invite clients to the portal.');
            }

            const resolvedTarget = await this._resolveCanonicalPortalClientTarget(target);
            const client = resolvedTarget.client || null;
            const clientDisplayName = String(
                client?.companyName
                || client?.name
                || 'your business'
            ).trim();
            if (!client?.id) {
                throw new Error('Create or link the client before sending portal invitations.');
            }

            const matchedContact = options?.contactId
                ? (client?.contacts?.find((contact) => contact.id === options.contactId)
                    || null)
                : null;
            const recipientName = String(
                options?.contactName
                || matchedContact?.name
                || client?.contactName
                || clientDisplayName
                || ''
            ).trim();
            const businessName = clientDisplayName;

            const recipientEmail = this._normalizeClientEmail(options?.contactEmail || client?.email);
            if (!recipientEmail) {
                throw new Error('Client email is required to send a portal invitation.');
            }

            const inviteContext = await this._resolveInviteLink({
                client,
                contactId: options?.contactId,
                contactEmail: recipientEmail,
                contactName: options?.contactName,
                portalRole: options?.portalRole,
                tenantSlug: tenantStore.currentTenantId || tenantStore.currentTenant?.id || undefined,
            });

            try {
                await $fetch('/api/send-invite', {
                    method: 'POST',
                    headers: await getRequiredAuthHeaders(),
                    body: {
                        tenantId: this._getTenantId(),
                        to: recipientEmail,
                        inviterName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Team',
                        inviteLink: inviteContext.inviteLink,
                        workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                        clientName: businessName,
                        recipientName: inviteContext.recipientName || recipientName,
                        portalRole: inviteContext.portalRole,
                        template: 'client_portal_invite',
                    },
                });
            } catch (sendError: any) {
                throw new Error(
                    sendError?.data?.statusMessage
                    || sendError?.statusMessage
                    || sendError?.message
                    || 'Failed to send portal invitation.',
                );
            }
        },

        async provisionClientContactPortalAccess(target: string | PortalAccessTarget, options?: {
            contactId?: string;
            contactEmail?: string;
            contactName?: string;
        }): Promise<{ granted: boolean; invitationId: string; userCreated: boolean }> {
            const tenantStore = useTenantStore();
            const { $auth } = useNuxtApp();

            if (!tenantStore.currentTenantRole || !['owner', 'admin'].includes(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to grant client portal access.');
            }

            const resolvedTarget = await this._resolveCanonicalPortalClientTarget(target);
            if (!resolvedTarget.clientId) {
                throw new Error('Create or link the client before granting portal access.');
            }

            const email = this._normalizeClientEmail(options?.contactEmail);
            if (!email) {
                throw new Error('Client email is required to grant portal access.');
            }

            const authToken = await $auth.currentUser?.getIdToken();
            if (!authToken) {
                throw new Error('You need to be signed in to grant portal access.');
            }

            return await $fetch<{ granted: boolean; invitationId: string; userCreated: boolean }>('/api/client-portal/provision' as string, {
                method: 'POST',
                headers: {
                    authorization: `Bearer ${authToken}`,
                },
                body: {
                    tenantId: this._getTenantId(),
                    clientId: resolvedTarget.clientId,
                    contactId: options?.contactId,
                    email,
                    contactName: options?.contactName,
                },
            });
        },

        async sendClientContactPasswordReset(target: string | PortalAccessTarget, options: {
            contactId?: string;
            contactEmail: string;
            contactName?: string;
        }): Promise<{ sent: boolean }> {
            const tenantStore = useTenantStore();
            const { $auth } = useNuxtApp();

            if (!tenantStore.currentTenantRole || !['owner', 'admin'].includes(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to send client password resets.');
            }

            const resolvedTarget = await this._resolveCanonicalPortalClientTarget(target);
            const email = this._normalizeClientEmail(options.contactEmail);
            const authToken = await $auth.currentUser?.getIdToken();
            if (!resolvedTarget.clientId || !email || !authToken) {
                throw new Error('A saved Client contact and active session are required.');
            }

            return await $fetch<{ sent: boolean }>('/api/client-portal/password-reset', {
                method: 'POST',
                headers: { authorization: `Bearer ${authToken}` },
                body: {
                    tenantId: this._getTenantId(),
                    clientId: resolvedTarget.clientId,
                    contactId: options.contactId,
                    email,
                    contactName: options.contactName,
                },
            });
        },

        async sendProjectLivePortalEmail(params: {
            clientId: string;
            projectId: string;
            projectName: string;
            projectSummary?: string;
            dueDate?: string;
        }): Promise<{ ok: boolean, result?: unknown }> {
            const tenantStore = useTenantStore();
            const userStore = useUserStore();
            const authStore = useAuthStore();
            const client = this.clients.find((entry) => entry.id === params.clientId) || this.currentClient;
            if (!client) {
                throw new Error('Client not found.');
            }

            const recipientEmail = this._normalizeClientEmail(client.email);
            if (!recipientEmail) {
                throw new Error('Client email is required before sending the project live email.');
            }

            const inviteContext = await this._resolveInviteLink({
                client,
                projectId: params.projectId,
                tenantSlug: tenantStore.currentTenantId || tenantStore.currentTenant?.id || undefined,
            });

            const response = await $fetch('/api/send-invite', {
                method: 'POST',
                headers: await getRequiredAuthHeaders(),
                body: {
                    tenantId: this._getTenantId(),
                    to: recipientEmail,
                    template: 'project_shared',
                    workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                    clientName: client.name,
                    inviteLink: inviteContext.inviteLink,
                    inviterName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || tenantStore.currentTenant?.name || 'Vuey',
                    projectName: params.projectName,
                    projectSummary: params.projectSummary,
                    dueDate: params.dueDate,
                    actionLabel: 'View project',
                },
            });
            return response as { ok: boolean, result?: unknown };
        },

        async validatePortalInvitation(token: string): Promise<ClientPortalInvitation | null> {
            try {
                const response = await $fetch<{
                    valid: boolean;
                    invitation?: {
                        id: string;
                        tenantId: string;
                        clientId?: string;
                        clientName?: string;
                        contactId?: string;
                        contactName?: string;
                        email: string;
                        expiresAt: string;
                    };
                }>('/api/client-invitations/validate', {
                    query: { token },
                });

                if (!response.valid || !response.invitation) {
                    return null;
                }

                return {
                    id: response.invitation.id,
                    tenantId: response.invitation.tenantId,
                    clientId: response.invitation.clientId,
                    clientName: response.invitation.clientName,
                    contactId: response.invitation.contactId,
                    contactName: response.invitation.contactName,
                    email: response.invitation.email,
                    invitedBy: '',
                    invitedAt: new Date(),
                    expiresAt: new Date(response.invitation.expiresAt),
                    token,
                    status: 'pending',
                } as ClientPortalInvitation;
            } catch (error) {
                console.error('Error validating client portal invitation:', error);
                return null;
            }
        },

        async acceptPortalInvitation(
            params: {
                token: string;
            },
            password?: string,
        ): Promise<{
            accepted: boolean;
            tenantId: string;
            clientId: string;
            clientSlug: string;
        }> {
            const { $auth } = useNuxtApp();
            const invitation = await this.validatePortalInvitation(params.token);

            if (!invitation) {
                throw new Error('Invitation is invalid or has expired.');
            }

            const invitationEmail = this._normalizeClientEmail(invitation.email);
            if (!invitationEmail) {
                throw new Error('Invitation email is missing.');
            }

            const clientName = String(
                invitation.contactName
                || invitation.clientName
                || invitationEmail.split('@')[0]
                || 'Client'
            ).trim();
            const currentUser = $auth.currentUser;
            const currentEmail = String(currentUser?.email || '').trim().toLowerCase();
            let authUser = currentUser;

            if (authUser) {
                if (currentEmail !== invitationEmail) {
                    await signOut($auth);
                    authUser = null;
                }
            }

            if (!authUser) {
                if (!password) {
                    throw new Error('Please choose a password to create your account.');
                }

                const credential = await createUserWithEmailAndPassword($auth, invitationEmail, password);
                authUser = credential.user;
            }

            if (!authUser) {
                throw new Error('Unable to resolve the invited account.');
            }

            if (!authUser.displayName && clientName) {
                await updateProfile(authUser, {
                    displayName: clientName,
                });
            }

            if (!authUser.emailVerified) {
                try {
                    await useAuthStore().sendVerificationEmail({
                        redirectPath: '/portal',
                        tenantId: invitation.tenantId,
                    });
                } catch (verificationError) {
                    console.warn('Unable to send verification email for client portal invite:', verificationError);
                }
            }

            const authToken = await authUser.getIdToken();
            return await $fetch<{
                accepted: boolean;
                tenantId: string;
                clientId: string;
                clientSlug: string;
            }>('/api/client-portal/accept' as string, {
                method: 'POST',
                body: {
                    token: params.token,
                },
                headers: {
                    authorization: `Bearer ${authToken}`,
                },
            });
        },

        async listClientPortalAccess(target: string | PortalAccessTarget): Promise<ClientPortalAccessRecord[]> {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const token = await $auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('You need to be signed in to manage portal access.');
            }

            const resolvedTarget = await this._resolveCanonicalPortalClientTarget(target);
            if (!resolvedTarget.clientId) {
                throw new Error('Create or link the client before viewing portal access.');
            }
            const response = await $fetch<{ records: ClientPortalAccessRecord[] }>('/api/client-portal/access', {
                query: {
                    tenantId,
                    clientId: resolvedTarget.clientId,
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            return Array.isArray(response.records) ? response.records : [];
        },

        async syncClientPortalScopes(target: string | PortalAccessTarget): Promise<void> {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const token = await $auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('You need to be signed in to sync portal access.');
            }

            const resolvedTarget = await this._resolveCanonicalPortalClientTarget(target);
            if (!resolvedTarget.clientId) {
                throw new Error('Create or link the client before syncing portal access.');
            }
            await $fetch('/api/client-portal/sync-scopes', {
                method: 'POST',
                body: {
                    tenantId,
                    clientId: resolvedTarget.clientId,
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });
        },

        async revokeClientPortalAccess(target: string | PortalAccessTarget, invitationId: string): Promise<void> {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const token = await $auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('You need to be signed in to revoke portal access.');
            }

            const resolvedTarget = await this._resolveCanonicalPortalClientTarget(target);
            if (!resolvedTarget.clientId) {
                throw new Error('Create or link the client before revoking portal access.');
            }
            await $fetch('/api/client-portal/revoke', {
                method: 'POST',
                body: {
                    tenantId,
                    clientId: resolvedTarget.clientId,
                    invitationId,
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });
        },

        async startOnboarding(
            clientId: string,
            payload: {
                templateId: string;
                clientName: string;
                projectMode: 'existing' | 'new' | 'skip';
                projectTemplateId?: string;
                existingProjectId?: string;
                projectName: string;
                projectDescription?: string;
                ownerId?: string;
                ownerName?: string;
                sendWelcomeEmail?: boolean;
            },
        ) {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const token = await $auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('You need to be signed in to start onboarding.');
            }

            return await $fetch<{
                clientId: string;
                projectId: string;
                onboardingRunId: string;
                inviteLink?: string;
            }>(`/api/clients/${clientId}/start-onboarding`, {
                method: 'POST',
                body: {
                    tenantId,
                    ...payload,
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });
        },
    }
});
